import { Controller } from 'stimulus';

// The new Schedule's drags (Scheduling::PlanEdits, SchedulesController#plan_*):
// a bar dragged sideways sets the task's dates (its lock with Automation on),
// its right edge sets its Duration, the padlock unlocks it, a job row's handle
// moves the job in the queue, and a job's bracket dragged right sets its
// Earliest Start. Clicking a bar or a task's name opens its pop-up: why it
// sits there, then its Step, Person, Equipment and When as on the Tasks tab,
// each opening the task's own window in the pop-up's place (tasks-popup-editor
// on the same element, saved by plan_field), which brings the pop-up back
// showing the change. A row's Person, Equipment or Duration chip opens that
// window straight away. Their people and equipment are ranked by when the
// task could start. Each answer's note names what moved, with Undo. In focus (focus= in the URL), + Add a Job widens it and the
// status line's gaps spotlight their tasks.
//
// A bar dragged down onto the People and equipment strip (the drag fit
// preview) outlines on each row where the task would fit with that person or
// machine (plan_fits); within two days of the outline snaps to it, and the
// drop gives the task to that row (plan_drop): with Automation on, on the
// outline it goes where it fits, elsewhere it locks; with it off, the drop
// sets its dates.
//
// It sits beside #1712's schedule controller on the same element, which keeps
// the window, folding, chips and refreshes; that one's own editing stays off
// on this screen (data-schedule-can-edit="false").
export default class extends Controller {
  static targets = ['pop', 'needs', 'needsError', 'needsCount', 'toTemplates'];

  connect() {
    this.canEdit = this.data.get('canEdit') === 'true';
    this.automation = this.data.get('automation') === 'true';
    this.drag = null;
    this.lastUndo = null;
    this.popSeq = 0;
    // Clicking a bar opens its popover for everyone (the why); the drags only
    // for people who may change the Schedule
    this._click = (e) => this.onClick(e);
    this._dbl = () => this.closePop();
    this._key = (e) => this.onKey(e);
    this.needsView = 'task';
    // Needs info's "save answers on the templates too" (off until ticked)
    this.toTemplates = false;
    this._outside = (e) => this.onOutside(e);
    this.element.addEventListener('click', this._click);
    this.element.addEventListener('dblclick', this._dbl);
    document.addEventListener('keydown', this._key);
    // (pointerdown: a drag's pointerdown cancels the mousedown that follows)
    document.addEventListener('pointerdown', this._outside);
    // (the focus line's gap buttons show which spotlight is on)
    this._spot = () => this.markFocusSpots();
    this.element.addEventListener('schedule:spotlight', this._spot);
    // A task's window (tasks-popup-editor): open, it pins the task in the
    // strip, and opened from the pop-up it takes the pop-up's place; saved,
    // the note and Undo, the grid again, and the pop-up back with the change
    this._windowOpen = (e) => this.windowOpened(e.detail);
    this._windowClosed = (e) => this.windowClosed(e.detail || {});
    this._windowSaved = (e) => this.answered(e.detail);
    document.addEventListener('tasks:window-open', this._windowOpen);
    document.addEventListener('tasks:window-closed', this._windowClosed);
    document.addEventListener('tasks:window-saved', this._windowSaved);
    if (!this.canEdit) return;
    this._down = (e) => this.onDown(e);
    this._move = (e) => this.onMove(e);
    this._up = (e) => this.onUp(e, false);
    this._cancel = (e) => this.onUp(e, true);
    this.element.addEventListener('pointerdown', this._down);
    this.element.addEventListener('pointermove', this._move);
    this.element.addEventListener('pointerup', this._up);
    this.element.addEventListener('pointercancel', this._cancel);
  }

  disconnect() {
    this.element.removeEventListener('click', this._click);
    this.element.removeEventListener('dblclick', this._dbl);
    document.removeEventListener('keydown', this._key);
    document.removeEventListener('pointerdown', this._outside);
    this.element.removeEventListener('schedule:spotlight', this._spot);
    document.removeEventListener('tasks:window-open', this._windowOpen);
    document.removeEventListener('tasks:window-closed', this._windowClosed);
    document.removeEventListener('tasks:window-saved', this._windowSaved);
    if (!this._down) return;
    this.element.removeEventListener('pointerdown', this._down);
    this.element.removeEventListener('pointermove', this._move);
    this.element.removeEventListener('pointerup', this._up);
    this.element.removeEventListener('pointercancel', this._cancel);
  }

  // #1712's controller on the same element: the model, dates, note, refresh
  get board() {
    return this.application.getControllerForElementAndIdentifier(this.element, 'schedule');
  }

  // The People and equipment strip (schedule-strip): an open popover or chip window pins
  // its task's person and equipment there
  pinStrip(taskId) {
    const strip = this.application.getControllerForElementAndIdentifier(this.element, 'schedule-strip');
    if (strip) strip.pin(taskId);
  }

  // ----- starting a drag -----

  onDown(e) {
    if (e.button !== 0 || this.busy) return;
    const board = this.board;
    if (!board || !board.model || e.target.closest('.sch-lk')) return;
    const handle = e.target.closest('[data-prio-job]');
    if (handle) { this.startPriority(e, handle); return; }
    const bracket = e.target.closest('.sch-sum[data-earliest-job]');
    if (bracket) { this.startBar(e, bracket, 'earliest'); return; }
    const bar = e.target.closest('.sch-pb.ed[data-task]');
    if (!bar) return;
    const mode = e.target.closest('.sch-pe') ? 'resize' : bar.dataset.mode;
    if (mode !== 'move' && mode !== 'resize') return;
    this.startBar(e, bar, mode);
  }

  startBar(e, el, mode) {
    const tl = el.parentElement;
    const rect = tl.getBoundingClientRect();
    this.drag = {
      mode, el, tl, x0: e.clientX, y0: e.clientY, moved: false, dd: 0, pid: e.pointerId,
      dayPx: rect.width / this.board.model.days, s0: Number(el.dataset.s), e0: Number(el.dataset.e),
      left: el.style.left, width: el.style.width
    };
    el.setPointerCapture(e.pointerId);
    e.preventDefault();
  }

  startPriority(e, handle) {
    const row = handle.closest('.sch-job');
    this.drag = { mode: 'priority', el: row, jobId: handle.dataset.prioJob, x0: e.clientX, y0: e.clientY, moved: false, pid: e.pointerId };
    handle.setPointerCapture(e.pointerId);
    e.preventDefault();
  }

  // ----- while dragging -----

  onMove(e) {
    const d = this.drag;
    if (!d) return;
    const dx = e.clientX - d.x0;
    const dy = e.clientY - d.y0;
    if (!d.moved && Math.abs(dx) < 4 && Math.abs(dy) < 4) return;
    if (!d.moved) {
      d.moved = true;
      // (an open popover's form would put the dragged dates back on Save)
      this.closePop();
      d.el.classList.add('sch-dragging');
      d.label = document.createElement('div');
      d.label.className = 'sch-drag-label';
      (d.mode === 'priority' ? this.board.grid : d.tl).appendChild(d.label);
      // (a bar striped through today moves on its own dates: data-pe, its end)
      if (d.mode === 'move' && d.el.dataset.pe !== undefined) this.toOwnDates(d);
    }
    if (d.mode === 'priority') { this.movePriority(e); return; }
    d.dd = Math.round(dx / d.dayPx);
    const b = this.board;
    if (d.mode === 'move' && this.overStrip(e, d)) return;
    if (d.mode === 'move' || d.mode === 'earliest') {
      d.el.style.transform = `translateX(${d.dd * d.dayPx}px)`;
      const s = d.s0 + d.dd;
      if (d.mode === 'move') this.label(d, d.dd ? `${this.automation ? 'Lock start' : 'Start'} ${b.fmt(s)}` : '');
      else if (d.dd) this.label(d, s <= b.model.today ? 'Clear Earliest Start' : `Earliest Start ${b.fmt(s)}`);
      else this.label(d, '');
      return;
    }
    // resize: the end follows the pointer, never before the start (a
    // started task's, never before today)
    const started = d.el.classList.contains('now') || d.el.classList.contains('over');
    const ne = Math.max(d.e0 + d.dd, started ? Math.max(d.s0, b.model.today) : d.s0);
    d.ne = ne;
    const l = Math.max(d.s0, 0);
    const r = Math.min(ne + 1, b.model.days);
    if (r > l) { d.el.style.left = b.pct(l); d.el.style.width = b.pctW(r - l); }
    const n = ne - d.s0 + 1;
    this.label(d, `Ends ${b.fmt(ne)} · ${n} ${n === 1 ? 'day' : 'days'}`);
  }

  toOwnDates(d) {
    const b = this.board;
    const l = Math.max(d.s0, 0);
    const r = Math.min(Number(d.el.dataset.pe) + 1, b.model.days);
    if (r > l) d.el.style.width = b.pctW(r - l);
    d.ext = d.el.querySelector('.sch-ext');
    if (d.ext) d.ext.hidden = true;
  }

  // ----- the drag fit preview -----

  get stripRows() {
    const strip = this.element.querySelector('[data-strip]');
    const body = strip && strip.querySelector('[data-strip-body]');
    return body && !body.hidden ? Array.from(strip.querySelectorAll('[data-strip-res]')) : [];
  }

  // Where the task fits on the strip's rows, asked for when the pointer
  // first reaches the strip: the rows in view first, at most fitsLimit of
  // them. A row tried with no room comes back null; one not tried is absent.
  loadFits(d) {
    const rows = this.stripRows;
    d.fitsAsked = true;
    if (!rows.length) return;
    const box = this.element.querySelector('[data-strip-scroller]').getBoundingClientRect();
    const inView = (r) => { const rr = r.getBoundingClientRect(); return rr.bottom > box.top && rr.top < box.bottom; };
    const ordered = rows.filter(inView).concat(rows.filter((r) => !inView(r)));
    const params = new URLSearchParams();
    ordered.slice(0, Number(this.data.get('fitsLimit')) || 40).forEach((r) => params.append('keys[]', r.dataset.stripRes));
    d.abort = window.AbortController ? new AbortController() : null;
    fetch(`${this.data.get('fitsUrl').replace('TASK', d.el.dataset.task)}?${params}`,
      { credentials: 'same-origin', headers: { Accept: 'application/json' }, signal: d.abort ? d.abort.signal : undefined })
      .then((r) => r.json())
      .then((json) => {
        if (this.drag !== d || !json.ok) return;
        d.fits = {};
        Object.keys(json.fits).forEach((key) => {
          const f = json.fits[key];
          d.fits[key] = f ? [this.dayIndex(f[0]), this.dayIndex(f[1])] : null;
        });
        this.drawFits(d);
        // (the pointer may be resting on a row: work out its drop again)
        if (d.lastOver) this.overStrip(d.lastOver, d);
      })
      .catch(() => {});
  }

  dayIndex(iso) {
    const p = iso.split('-').map(Number);
    return Math.round((new Date(p[0], p[1] - 1, p[2]) - this.board.startDate) / 86400000);
  }

  drawFits(d) {
    const b = this.board;
    this.stripRows.forEach((row) => {
      const f = d.fits[row.dataset.stripRes];
      if (!f) return;
      const tl = row.querySelector('.sch-tl');
      const el = document.createElement('span');
      if (f[1] < 0 || f[0] >= b.model.days) {
        el.className = 'sch-fit later';
        el.textContent = f[0] >= b.model.days ? `fits from ${b.fmt(f[0])} ›` : '';
      } else {
        const l = Math.max(f[0], 0);
        const r = Math.min(f[1], b.model.days - 1);
        el.className = 'sch-fit';
        el.style.left = b.pct(l);
        el.style.width = b.pctW(r - l + 1);
        el.textContent = r > l ? 'fits' : '';
        el.title = `Fits ${b.range(f[0], f[1])}`;
      }
      tl.appendChild(el);
    });
  }

  clearFits() {
    this.element.querySelectorAll('.sch-fit, .sch-fit-label').forEach((el) => el.remove());
    this.element.querySelectorAll('[data-strip-res].drop-over').forEach((el) => el.classList.remove('drop-over'));
  }

  // Over a strip row: that row is the drop, at the outline when the start is
  // within two days of it. Returns whether the pointer is over the strip.
  overStrip(e, d) {
    const box = this.element.querySelector('[data-strip-scroller]');
    const r = box && box.getBoundingClientRect();
    const inside = r && e.clientY >= r.top && e.clientY <= r.bottom && e.clientX >= r.left && e.clientX <= r.right;
    this.element.querySelectorAll('[data-strip-res].drop-over').forEach((el) => el.classList.remove('drop-over'));
    this.element.querySelectorAll('.sch-fit-label').forEach((el) => el.remove());
    d.drop = null;
    if (!inside) { d.label.hidden = !d.label.textContent; return false; }
    if (!d.fitsAsked) this.loadFits(d);
    d.lastOver = { clientX: e.clientX, clientY: e.clientY };
    d.el.style.transform = `translateX(${d.dd * d.dayPx}px)`;
    d.label.hidden = true;
    const under = document.elementFromPoint(e.clientX, e.clientY);
    const row = under && under.closest('[data-strip-res]');
    if (!row) return true;
    const b = this.board;
    const key = row.dataset.stripRes;
    const f = d.fits && d.fits[key];
    const want = d.s0 + d.dd;
    const onFit = !!f && Math.abs(want - f[0]) <= 2;
    const start = onFit ? f[0] : want;
    d.drop = { key, start, onFit };
    row.classList.add('drop-over');
    const nameEl = row.querySelector('.sch-sn-name');
    const name = nameEl ? nameEl.textContent.trim() : '';
    const who = key.charAt(0) === 'p' ? `Assign to ${name}` : `Use ${name}`;
    let fits;
    if (!d.fits) fits = 'finding room…';
    else if (f) fits = `fits from ${b.fmt(f[0])}`;
    else fits = key in d.fits ? 'no room found' : 'not tried (too many rows)';
    const when = this.automation && onFit ? '' : ` · ${this.automation ? 'lock start' : 'start'} ${b.fmt(start)}`;
    const tip = document.createElement('div');
    tip.className = 'sch-drag-label sch-fit-label';
    tip.textContent = `${who}${when} · ${fits}`;
    tip.style.left = b.pct(Math.max(0, Math.min(start, b.model.days - 1)));
    row.querySelector('.sch-tl').appendChild(tip);
    return true;
  }

  label(d, text) {
    if (!d.label) return;
    d.label.textContent = text;
    d.label.hidden = !text;
    if (d.mode !== 'priority') d.label.style.left = d.el.style.left;
  }

  // The job rows in the queue, and where the pointer would put the dragged one
  movePriority(e) {
    const d = this.drag;
    const rows = Array.from(this.board.grid.querySelectorAll('.sch-job[data-priority]'));
    let idx = rows.length;
    for (let i = 0; i < rows.length; i += 1) {
      const rr = rows[i].getBoundingClientRect();
      if (e.clientY < rr.top + rr.height / 2) { idx = i; break; }
    }
    const grid = this.board.grid.getBoundingClientRect();
    const last = rows[rows.length - 1];
    const y = idx < rows.length ? rows[idx].getBoundingClientRect().top : (last ? last.getBoundingClientRect().bottom : grid.top);
    if (!d.line) {
      d.line = document.createElement('div');
      d.line.className = 'sch-prio-line';
      this.board.grid.appendChild(d.line);
    }
    d.line.style.top = `${y - grid.top - 1}px`;
    const target = idx < rows.length ? rows[idx] : null;
    d.beforeId = target ? target.dataset.jobId : '';
    d.label.style.top = `${y - grid.top + 4}px`;
    d.label.style.left = '24px';
    // (named, not numbered: the queue holds jobs this person may not see)
    const link = target && target.querySelector('.sch-jlink');
    if (target && target.dataset.jobId === d.jobId) d.label.textContent = '';
    else d.label.textContent = target ? `Ahead of ${link ? link.textContent.trim() : 'that job'}` : 'To the end of the queue';
  }

  // ----- dropping -----

  onUp(e, cancel) {
    const d = this.drag;
    if (!d) return;
    this.drag = null;
    if (d.label) d.label.remove();
    if (d.line) d.line.remove();
    this.clearFits();
    if (d.abort) d.abort.abort();
    d.el.classList.remove('sch-dragging');
    const reset = () => {
      d.el.style.transform = '';
      if (d.left !== undefined) { d.el.style.left = d.left; d.el.style.width = d.width; }
      if (d.ext) d.ext.hidden = false;
    };
    if (cancel || !d.moved) { reset(); return; }
    // (let go over the strip: a row takes it; between rows, nothing happens)
    if (d.mode === 'move' && d.drop !== undefined && this.overStripAt(e)) {
      this.justDragged = true;
      setTimeout(() => { this.justDragged = false; }, 0);
      if (!d.drop) { reset(); return; }
      // (on the outline with Automation on, the planner puts it there; a
      // locked task needs the new start to leave its lock)
      const start = this.automation && d.drop.onFit && !d.el.classList.contains('locked') ? '' : this.board.iso(d.drop.start);
      this.send(this.url('drop', d.el.dataset.task), 'PATCH', { to: d.drop.key, start_on: start }, reset);
      return;
    }
    // (the click that follows a drag doesn't open the popover)
    this.justDragged = true;
    setTimeout(() => { this.justDragged = false; }, 0);
    const b = this.board;
    if (d.mode === 'priority') {
      const rows = Array.from(b.grid.querySelectorAll('.sch-job[data-priority]'));
      const from = rows.findIndex((r) => r.dataset.jobId === d.jobId);
      const next = rows[from + 1];
      if (d.beforeId === d.jobId || (next && d.beforeId === next.dataset.jobId) || (!next && d.beforeId === '')) return;
      this.send(this.url('priority', d.jobId), 'PATCH', { before_id: d.beforeId });
      return;
    }
    if (d.mode === 'move') {
      if (!d.dd) { reset(); return; }
      this.send(this.url('task', d.el.dataset.task), 'PATCH', { op: 'move', start_on: b.iso(d.s0 + d.dd) }, reset);
    } else if (d.mode === 'resize') {
      if (d.ne === undefined || d.ne === d.e0) { reset(); return; }
      this.send(this.url('task', d.el.dataset.task), 'PATCH', { op: 'resize', start_on: b.iso(d.s0), end_on: b.iso(d.ne) }, reset);
    } else if (d.mode === 'earliest') {
      if (!d.dd) { reset(); return; }
      const s = d.s0 + d.dd;
      this.send(this.url('earliest', d.el.dataset.earliestJob), 'PATCH', { start_on: s <= b.model.today ? '' : b.iso(s) }, reset);
    }
  }

  // The padlock unlocks, so the planner places the task; a bar (or an On
  // hold tag, or an off-screen note) or the task's name opens its pop-up
  onClick(e) {
    const lock = e.target.closest('.sch-lk[data-unlock]');
    if (lock) {
      if (!this.canEdit) return;
      e.preventDefault();
      e.stopPropagation();
      this.closePop();
      this.send(this.url('task', lock.dataset.unlock), 'PATCH', { op: 'unlock' });
      return;
    }
    if (this.justDragged || (this.hasPopTarget && this.popTarget.contains(e.target))) return;
    // (the name is still the task form's link, which double-clicking a bar
    // follows: only a real click opens the pop-up)
    const name = e.target.closest('a[data-pop-task]');
    if (name && e.isTrusted) {
      e.preventDefault();
      e.stopPropagation();
      this.openPop(name.dataset.popTask, name);
      return;
    }
    const bar = e.target.closest('.sch-bar[data-task]');
    if (bar) this.openPop(bar.dataset.task, bar);
  }

  // ----- focus -----

  // + Add a Job: the Schedule focused on one more job, in place
  focusAdd(e) {
    const url = e.currentTarget.value;
    if (url) this.board.go(url, null, true);
  }

  // A gap on a focused job's line spotlights those tasks (the chip's case)
  focusSpot(e) {
    e.preventDefault();
    const b = this.board;
    const kind = e.currentTarget.dataset.focusSpot;
    if (b.spot.active[kind]) delete b.spot.active[kind];
    else {
      b.spot.active[kind] = true;
      b.spot.idx[kind] = b.spot.idx[kind] || 0;
      b.spot.scroll = true;
    }
    b.renderChips();
    b.applySpotlight();
  }

  markFocusSpots() {
    const b = this.board;
    if (!b) return;
    this.element.querySelectorAll('[data-focus-spot]').forEach((btn) => {
      btn.classList.toggle('on', !!b.spot.active[btn.dataset.focusSpot]);
    });
  }

  // The focus in the page's address (Needs Info follows it)
  get focusParam() {
    const rows = new URL(this.board.data.get('rowsUrl'), window.location.href);
    return rows.searchParams.get('focus') || '';
  }

  // ----- People & equipment -----

  // The People and Equipment sections fold to their band (remembered, as job
  // rows are, in #1712's collapsed list)
  toggleSection(e) {
    const row = e.currentTarget;
    const key = row.dataset.key;
    const open = !row.classList.contains('open');
    row.classList.toggle('open', open);
    const body = this.board.grid.querySelector(`[data-sec-body="${key}"]`);
    if (body) body.hidden = !open;
    if (open) this.board.collapsed.delete(key); else this.board.collapsed.add(key);
    this.board.savePrefs();
  }

  // ----- Needs info -----

  openNeeds(e) {
    if (e) e.preventDefault();
    this.closePop();
    this.loadNeeds();
  }

  // The panel again (after an answer, or By Task / By Job); the Later section
  // stays as it was
  loadNeeds() {
    if (!this.hasNeedsTarget) return;
    const later = this.needsTarget.querySelector('.snp-later');
    const laterOpen = !!later && !later.hidden;
    const focus = this.focusParam;
    const url = `${this.data.get('needsUrl')}?mode=${this.needsView}${focus ? `&focus=${encodeURIComponent(focus)}` : ''}`;
    fetch(url, { credentials: 'same-origin', headers: { Accept: 'text/html' } })
      .then((r) => { if (!r.ok || r.redirected) throw new Error(r.status); return r.text(); })
      .then((html) => {
        if (html.indexOf('class="snp"') < 0) throw new Error('not the panel');
        this.needsTarget.innerHTML = html;
        this.needsTarget.hidden = false;
        if (this.hasToTemplatesTarget) {
          this.toTemplatesTarget.checked = this.toTemplates;
          this.toTemplatesTarget.addEventListener('change', (ev) => { this.toTemplates = ev.target.checked; });
        }
        if (laterOpen) this.toggleLater(true);
        const box = this.needsTarget.querySelector('.snp');
        if (box && this.hasNeedsCountTarget) {
          this.needsCountTarget.textContent = box.dataset.count;
          this.needsCountTarget.classList.toggle('zero', box.dataset.count === '0');
        }
      })
      .catch(() => this.note('<span class="sch-conf">Needs info could not be opened.</span>'));
  }

  needsMode(e) {
    this.needsView = e.currentTarget.dataset.mode;
    this.loadNeeds();
  }

  needsLater(e) {
    e.preventDefault();
    const later = this.needsTarget.querySelector('.snp-later');
    this.toggleLater(!!later && later.hidden);
  }

  toggleLater(open) {
    const later = this.needsTarget.querySelector('.snp-later');
    const btn = this.needsTarget.querySelector('[data-action="schedule-plan#needsLater"]');
    if (later) later.hidden = !open;
    if (btn) btn.textContent = open ? 'Hide' : 'Show';
  }

  // Closing it: what joined the Schedule until now is no longer New
  closeNeeds() {
    if (!this.hasNeedsTarget || this.needsTarget.hidden) return;
    this.needsTarget.hidden = true;
    this.needsTarget.innerHTML = '';
    fetch(this.data.get('needsSeenUrl'), { method: 'POST', credentials: 'same-origin', headers: { 'X-CSRF-Token': this.csrf() } })
      .then(() => this.board.refresh())
      .catch(() => {});
  }

  // An answer, saved as soon as it's picked or typed, on every task it names.
  // Answers given while one is saving wait their turn (never dropped); the
  // panel reloads once they're all in.
  answer(e) {
    const ctl = e.currentTarget;
    const value = ctl.value.trim();
    if (!value) return;
    this.answers = this.answers || [];
    this.answers.push({ field: ctl.dataset.field, value, task_ids: ctl.dataset.tasks.split(','), templates: this.toTemplates ? '1' : '0' });
    if (this.hasNeedsErrorTarget) this.needsErrorTarget.hidden = true;
    if (!this.answering) this.nextAnswer();
  }

  nextAnswer() {
    const item = this.answers.shift();
    if (!item) {
      this.answering = false;
      this.reloadNeedsWhenIdle();
      return;
    }
    this.answering = true;
    fetch(this.data.get('answersUrl'), {
      method: 'PATCH',
      credentials: 'same-origin',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json', 'X-CSRF-Token': this.csrf() },
      body: JSON.stringify(item)
    })
      .then((r) => r.json().catch(() => ({ ok: false })))
      .then((json) => {
        if (json.ok) {
          this.lastUndo = json.undo && json.undo.length ? json.undo : null;
          this.note(this.describe(json), !!this.lastUndo);
          this.board.refresh();
        } else if (this.hasNeedsErrorTarget) {
          this.needsErrorTarget.textContent = json.error || 'That answer could not be saved.';
          this.needsErrorTarget.hidden = false;
        }
        this.nextAnswer();
      })
      .catch(() => this.nextAnswer());
  }

  // Use / Use All Suggestions: the server works out each task's suggestion
  // again and saves it (plan_suggestions); the note says what, with Undo
  // (a refusal shows in the panel, as an answer's does; not while an
  // answer is still saving)
  useSuggestions(e) {
    e.preventDefault();
    if (this.busy || this.answering) return;
    const btn = e.currentTarget;
    const body = { task_ids: btn.dataset.tasks.split(',').filter(Boolean), templates: this.toTemplates ? '1' : '0' };
    if (btn.dataset.field) body.field = btn.dataset.field;
    if (this.hasNeedsErrorTarget) this.needsErrorTarget.hidden = true;
    this.busy = true;
    fetch(this.data.get('suggestionsUrl'), {
      method: 'PATCH',
      credentials: 'same-origin',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json', 'X-CSRF-Token': this.csrf() },
      body: JSON.stringify(body)
    })
      .then((r) => r.json().catch(() => ({ ok: false })))
      .then((json) => {
        this.busy = false;
        if (json.ok) { this.answered(json); return; }
        this.needsProblem(json.error || 'Those suggestions could not be saved.');
      })
      .catch(() => {
        this.busy = false;
        this.needsProblem('Those suggestions could not be saved. Check your connection and try again.');
      });
  }

  needsProblem(text) {
    if (this.hasNeedsErrorTarget && !this.needsTarget.hidden) {
      this.needsErrorTarget.textContent = text;
      this.needsErrorTarget.hidden = false;
    } else this.note(`<span class="sch-conf">${this.escape(text)}</span>`);
  }

  // The panel again, but not under someone typing in it: then once they
  // leave the field (its change, if any, answers first)
  reloadNeedsWhenIdle() {
    if (!this.hasNeedsTarget || this.needsTarget.hidden) return;
    const active = document.activeElement;
    if (active && this.needsTarget.contains(active) && active.matches('input[type="text"]') && active.value.trim()) {
      active.addEventListener('blur', () => setTimeout(() => { if (!this.answering) this.reloadNeedsWhenIdle(); }, 0), { once: true });
      return;
    }
    this.loadNeeds();
  }

  // ----- the popover -----

  get popOpen() { return this.hasPopTarget && !this.popTarget.hidden; }

  openPop(taskId, anchor) {
    const rows = new URL(this.board.data.get('rowsUrl'), window.location.href);
    this.loadPop(`${this.data.get('popoverUrl').replace('TASK', taskId)}${rows.search}`, anchor, taskId);
  }

  // at: where it was (left, top), to bring it back there after a window's save
  loadPop(url, anchor, taskId, at) {
    if (!this.hasPopTarget) return;
    const seq = ++this.popSeq;
    if (anchor) this.popAnchor = anchor;
    this.pinStrip(taskId);
    fetch(url, { credentials: 'same-origin', headers: { Accept: 'text/html' } })
      // (a redirect, such as to sign in, is a whole page, not the popover)
      .then((r) => { if (!r.ok || r.redirected) throw new Error(r.status); return r.text(); })
      .then((html) => {
        if (seq !== this.popSeq) return;
        if (html.indexOf('class="spp') < 0) throw new Error('not a popover');
        this.popTarget.innerHTML = html;
        this.popTarget.hidden = false;
        if (at) {
          this.popTarget.style.left = at.left;
          this.popTarget.style.top = at.top;
        } else {
          this.placePop(anchor);
        }
        const field = this.popTarget.querySelector('input[autofocus]');
        if (field) field.focus();
      })
      .catch(() => {
        if (seq !== this.popSeq) return;
        this.pinStrip(null);
        this.note('<span class="sch-conf">That task could not be opened.</span>');
      });
  }

  // Under the bar, or above it when there's no room, kept on screen and below
  // the app's top bar
  placePop(anchor) {
    const pop = this.popTarget;
    const r = anchor.getBoundingClientRect();
    const w = pop.offsetWidth;
    const h = pop.offsetHeight;
    const minTop = 72;
    const left = Math.max(8, Math.min(r.left, window.innerWidth - w - 16));
    let top = r.bottom + 6;
    if (top + h > window.innerHeight - 8) top = Math.max(minTop, r.top - h - 6);
    pop.style.left = `${left}px`;
    pop.style.top = `${top}px`;
  }

  // Escape closes the popover, but not while typing in it or in one of its
  // pickers (Escape closes the picker there)
  onKey(e) {
    if (e.key !== 'Escape') return;
    if (e.target.closest && e.target.closest('input, textarea, select, .select2-container')) return;
    if (this.popOpen) this.closePop();
    else if (this.hasNeedsTarget && !this.needsTarget.hidden) this.closeNeeds();
  }

  // A window opened from inside the pop-up takes its place; one opened from
  // a chip closes it
  windowOpened({ taskId, inPlace }) {
    if (inPlace && this.popOpen) {
      this.heldPop = { taskId, at: { left: this.popTarget.style.left, top: this.popTarget.style.top } };
      this.popTarget.hidden = true;
    } else {
      this.heldPop = null;
      this.closePop();
    }
    this.pinStrip(taskId);
  }

  // Back from the window: as it was (Cancel, Escape), showing the change
  // (saved), or closed with it (a click elsewhere)
  windowClosed({ why }) {
    const held = this.heldPop;
    this.heldPop = null;
    if (!held) {
      if (!this.popOpen) this.pinStrip(null);
      return;
    }
    if (why === 'cancel') {
      this.popTarget.hidden = false;
    } else if (why === 'saved') {
      const rows = new URL(this.board.data.get('rowsUrl'), window.location.href);
      this.loadPop(`${this.data.get('popoverUrl').replace('TASK', held.taskId)}${rows.search}`, null, held.taskId, held.at);
    } else if (why !== 'replace') {
      this.closePop();
    }
  }

  closePop() {
    this.popSeq += 1;
    this.pinStrip(null);
    if (!this.hasPopTarget || this.popTarget.hidden) return;
    this.popTarget.hidden = true;
    this.popTarget.innerHTML = '';
  }

  // A click outside closes it (not inside its pickers' dropdowns, which open
  // on the page body)
  onOutside(e) {
    if (!this.popOpen) return;
    if (this.popTarget.contains(e.target) || e.target.closest('.select2-container, .modal')) return;
    if (this.popAnchor && this.popAnchor.contains(e.target)) return;
    this.closePop();
  }

  // Put on Hold / Take off Hold
  hold(e) {
    e.preventDefault();
    const box = e.target.closest('[data-task-id]');
    if (!box) return;
    this.closePop();
    this.send(this.url('task', box.dataset.taskId), 'PATCH', { op: e.currentTarget.dataset.hold });
  }

  overStripAt(e) {
    const box = this.element.querySelector('[data-strip-scroller]');
    const r = box && box.getBoundingClientRect();
    return !!r && e.clientY >= r.top && e.clientY <= r.bottom && e.clientX >= r.left && e.clientX <= r.right;
  }

  url(kind, id) {
    const raw = this.data.get(`${kind}Url`);
    return raw.replace('TASK', id).replace('JOB', id);
  }

  // ----- saving, the note, Undo -----

  send(url, method, body, onFail) {
    this.busy = true;
    this.note('Saving…');
    fetch(url, {
      method,
      credentials: 'same-origin',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json', 'X-CSRF-Token': this.csrf() },
      body: JSON.stringify(body)
    })
      .then((r) => r.json().catch(() => ({ ok: false })))
      .then((json) => {
        this.busy = false;
        if (!json.ok) {
          if (onFail) onFail();
          this.note(`<span class="sch-conf">${this.escape(json.error || 'That change could not be saved.')}</span>`);
          return;
        }
        this.answered(json);
      })
      .catch(() => {
        this.busy = false;
        if (onFail) onFail();
        this.note('<span class="sch-conf">That change could not be saved. Check your connection and try again.</span>');
      });
  }

  // A saved change: the note (with Undo when there's something to put back)
  // and the grid again
  answered(json) {
    this.lastUndo = json.undo && json.undo.length ? json.undo : null;
    this.note(this.describe(json), !!this.lastUndo);
    this.board.refresh();
    // (an open Needs Info shows what that change answered)
    if (this.hasNeedsTarget && !this.needsTarget.hidden && !this.answering) this.reloadNeedsWhenIdle();
  }

  undo(event) {
    if (event) event.preventDefault();
    if (!this.lastUndo || this.busy) return;
    const items = this.lastUndo;
    this.lastUndo = null;
    this.send(this.data.get('undoUrl'), 'POST', { undo: items });
  }

  // What changed, then what else the plan moved (the planner's change
  // report, or with Automation off what moved on the chain)
  describe(json) {
    let html = (json.changes || []).map((c) => this.escape(c)).join(' ');
    const lines = (json.plan && json.plan.lines) || [];
    if (lines.length) {
      const shown = lines.slice(0, 4).map((l) => this.escape(l)).join('; ');
      html += ` <span class="sch-note-more" title="${this.escape(lines.join('\n'))}">Also: ${shown}${lines.length > 4 ? `; and ${lines.length - 4} more` : ''}.</span>`;
    }
    return html;
  }

  note(html, undoable) {
    const b = this.board;
    if (!b || !b.hasNoteTarget) return;
    b.noteTarget.innerHTML = html + (undoable ? ' <button type="button" class="link focus:outline-none" data-action="click->schedule-plan#undo">Undo</button>' : '');
  }

  csrf() {
    const meta = document.querySelector('meta[name="csrf-token"]');
    return meta ? meta.content : '';
  }

  escape(s) {
    return String(s).replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;').replace(/"/g, '&quot;');
  }
}
